'use client'
import { sanitizeHTML } from '@/lib/utils'

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Image from 'next/image'

interface HomeVidTestProps {
  heading?: {
    title?: string
  }
  content_collection?: {
    id: string
    site_content_collections_id: {
      collection: {
        item: {
          title?: string | null
          text?: string | null
          embed_url?: string | null
        }
      }[]
    }
  }[]
  button?: {
    href: string
    label: string
  }
}

export const fields = [
  'heading.title',
  'content_collection.id',
  'content_collection.site_content_collections_id.collection.item:site_content.title',
  'content_collection.site_content_collections_id.collection.item:site_content.text',
  'content_collection.site_content_collections_id.collection.item:videos.embed_url',
  'button.href',
  'button.label'
]

// Function to check if URL is a YouTube link
const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be')
}

// Function to extract YouTube video ID from URL
const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  
  return (match && match[2].length === 11)
    ? match[2]
    : null
}

// Function to create YouTube embed URL
const getYouTubeEmbedUrl = (videoId: string, origin?: string): string => {
  const baseUrl = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&controls=1&showinfo=0&rel=0&autoplay=1&modestbranding=1&playsinline=1`
  return origin ? `${baseUrl}&origin=${encodeURIComponent(origin)}` : baseUrl
}

// Function to get YouTube thumbnail URL
const getYouTubeThumbnailUrl = (videoId: string, quality: 'default' | 'hq' | 'maxres' = 'maxres'): string => {
  if (quality === 'default') {
    return `https://img.youtube.com/vi/${videoId}/0.jpg`
  } else if (quality === 'hq') {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
  } else {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  }
}

export default function HomeVidTest({ data }: { data: HomeVidTestProps }) {
  const [activeVideo, setActiveVideo] = useState<number>(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [isYouTube, setIsYouTube] = useState(false)
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [youtubeInitialized, setYoutubeInitialized] = useState(false)
  const [origin, setOrigin] = useState('')

  // Clean up interval
  const cleanupInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  // Check for YouTube video whenever active video changes
  useEffect(() => {
    const videoUrl = getVideoUrl(activeVideo)
    const youtubeVideo = isYouTubeUrl(videoUrl)
    
    setIsYouTube(youtubeVideo)
    setIsPlaying(false)
    setProgress(0)
    setCurrentTime(0)
    setYoutubeInitialized(false)
    
    // Clean up interval
    cleanupInterval()
    
    return () => {
      cleanupInterval()
    }
  }, [activeVideo])

  useEffect(() => {
    setOrigin(window.location.origin)
  }, [])

  // Set up YouTube message listener
  useEffect(() => {
    const handleYouTubeMessage = (event: MessageEvent) => {
      // Only process messages from YouTube
      if (!event.data || typeof event.data !== 'string') {
        return
      }
      
      try {
        // Try to parse the data as JSON
        if (event.data.startsWith('{')) {
          const data = JSON.parse(event.data)
          
          if (data.event === 'onStateChange') {
            // YouTube states: -1 (not started), 0 (ended), 1 (playing), 2 (paused), 3 (buffering)
            if (data.info === 1) {
              setIsPlaying(true)
            } else if (data.info === 2 || data.info === 0) {
              setIsPlaying(false)
            }
            
            if (data.info === 0) { // ended
              setProgress(0)
              setCurrentTime(0)
            }
          } else if (data.event === 'onReady') {
            // Video is ready
            if (iframeRef.current) {
              setDuration(data.duration || 0)
            }
          }
        } else if (event.data.includes('PLAYING')) {
          setIsPlaying(true)
        } else if (event.data.includes('PAUSED')) {
          setIsPlaying(false)
        } else if (event.data.includes('ENDED')) {
          setIsPlaying(false)
          setProgress(0)
          setCurrentTime(0)
        }
      } catch (error) {
        console.error('Error processing YouTube event:', error)
      }
    }
    
    window.addEventListener('message', handleYouTubeMessage)
    
    return () => {
      window.removeEventListener('message', handleYouTubeMessage)
    }
  }, [])

  const handleNextVideo = () => {
    setActiveVideo((prev) => (prev + 1) % (data.content_collection?.length || 0))
  }

  // Add close handler to stop video and return to initial state
  const handleCloseVideo = () => {
    if (isYouTube) {
      // Reset YouTube video state
      setYoutubeInitialized(false)
      setIsPlaying(false)
      setProgress(0)
      setCurrentTime(0)
      cleanupInterval()
    } else if (videoRef.current) {
      // Stop regular video
      videoRef.current.pause()
      videoRef.current.currentTime = 0
      setIsPlaying(false)
      setProgress(0)
      setCurrentTime(0)
    }
  }

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100
      setProgress(progress)
      setCurrentTime(videoRef.current.currentTime)
    }
  }

  // Initialize YouTube iframe if not already initialized
  const initializeYouTube = () => {
    if (isYouTube && !youtubeInitialized) {
      setYoutubeInitialized(true)
      setIsPlaying(true)
    }
  }

  // Toggle play/pause for both types of players
  const togglePlayPause = () => {
    if (isYouTube) {
      if (!youtubeInitialized) {
        // Initialize YouTube if it's the first play
        initializeYouTube()
        return
      }
    } else if (videoRef.current) {
      if (!isPlaying) {
        videoRef.current.play()
      } else {
        videoRef.current.pause()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const getTitle = (index: number) => {
    return data.content_collection?.[index]?.site_content_collections_id.collection.find(item => item.item.title)?.item.title || ''
  }

  const getText = (index: number) => {
    return data.content_collection?.[index]?.site_content_collections_id.collection.find(item => item.item.text)?.item.text || ''
  }

  const getVideoUrl = (index: number) => {
    return data.content_collection?.[index]?.site_content_collections_id.collection.find(item => item.item.embed_url)?.item.embed_url || ''
  }

  // Get YouTube embed URL for current video
  const getCurrentYouTubeEmbedUrl = () => {
    const videoUrl = getVideoUrl(activeVideo)
    const videoId = getYouTubeVideoId(videoUrl)
    return videoId ? getYouTubeEmbedUrl(videoId, origin) : ''
  }
  
  // Get current YouTube video ID
  const getCurrentYouTubeVideoId = () => {
    const videoUrl = getVideoUrl(activeVideo)
    return getYouTubeVideoId(videoUrl)
  }

  return (
		<section className='HomeVidTest relative bg-[#BEC3B7] overflow-hidden rounded-tr-[50px] lg:rounded-tr-none h-full'>
			<div className='pt-[2rem] pb-[4rem] lg:py-0 relative'>
				<div className='sld_slider_big_video'>
					{/* Container with responsive padding */}
					<div className='container lg:hidden'>
						<div className='text-4xl font-agopus mb-8'>
							{data.heading?.title || ''}
						</div>
					</div>

					{/* Main content container */}
					<div className='relative w-full aspect-[16/9] max-h-[90vh]'>
						<div className='absolute inset-0 z-[9]'>
							<div className='absolute inset-0 w-full h-full bg-sand-light'>
								<div className='h-full w-full'>
									<div className='relative h-full w-full'>
										{/* Left Content - Hidden on mobile */}
										<motion.div
											initial={{ opacity: 1, x: 0 }}
											animate={{
												opacity: isPlaying ? 0 : 1,
												x: isPlaying ? -100 : 0,
											}}
											transition={{ duration: 0.3 }}
											className='absolute left-12 top-[15%] w-[460px] z-10 hidden lg:block'
										>
											<h2 className='text-5xl font-agopus mb-16'>
												{data.heading?.title || ''}
											</h2>
											<p className='text-2xl font-brandon mb-8'>
												{getTitle(activeVideo)}
											</p>
											<div
												className='text-lg text-gray-slate leading-relaxed mb-12 prose'
												dangerouslySetInnerHTML={sanitizeHTML(
													getText(activeVideo)
												)}
											/>
											<div className='w-fit max-md:w-full'>
												<a
												href={data.button?.href || '#'}
												className='inline-block px-12 py-5 bg-brown rounded-lg text-xl text-white font-brandon hover:bg-brown/90 transition-colors w-full text-center'
											>
													{data.button?.label || ''}
												</a>
											</div>
										</motion.div>

										{/* Video Container */}
										<div className='absolute inset-0 w-full h-full overflow-hidden'>
											{/* Click Layer - only if video hasn't started */}
											{!youtubeInitialized && (
												<div
													className='absolute inset-0 z-[1] cursor-pointer'
													onClick={togglePlayPause}
													role="button"
													aria-label="Play video"
													tabIndex={0}
												/>
											)}

											<AnimatePresence mode='wait'>
												<motion.div
													key={activeVideo}
													initial={{ opacity: 0 }}
													animate={{ opacity: 1 }}
													exit={{ opacity: 0 }}
													transition={{ duration: 0.5, ease: 'easeInOut' }}
													className='absolute inset-0 w-full h-full'
												>
													{isYouTube ? (
                            youtubeInitialized ? (
                              // YouTube iframe with native controls
                              <iframe
                                ref={iframeRef}
                                src={getCurrentYouTubeEmbedUrl()}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            ) : (
                              // YouTube thumbnail (shows initially)
                              <div className="w-full h-full relative">
                                {getCurrentYouTubeVideoId() && (
                                  <Image
                                    src={getYouTubeThumbnailUrl(getCurrentYouTubeVideoId() || '')}
                                    alt={getTitle(activeVideo)}
                                    fill
                                    className="object-cover"
                                    unoptimized 
                                    priority
                                  />
                                )}
                              </div>
                            )
                          ) : (
                            // Native video player
                            <video
                              ref={videoRef}
                              src={getVideoUrl(activeVideo)}
                              className='w-full h-full object-cover'
                              controls={true} 
                              playsInline
                              preload='auto'
                              autoPlay={isPlaying}
                              onTimeUpdate={handleTimeUpdate}
                              onLoadedMetadata={() => {
                                if (videoRef.current) {
                                  setDuration(videoRef.current.duration)
                                }
                              }}
                              onEnded={() => {
                                setIsPlaying(false)
                                setProgress(0)
                                setCurrentTime(0)
                              }}
                            />
                          )}
												</motion.div>
											</AnimatePresence>

											{/* Play Button - only shown before initialization */}
											{!youtubeInitialized && isYouTube && (
												<button
													onClick={togglePlayPause}
													className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[72px] h-[72px] rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center z-[99]'
													aria-label="Play video"
												>
													<div className='w-0 h-0 border-t-[12px] border-b-[12px] border-l-[20px] border-l-white border-t-transparent border-b-transparent ml-2' />
												</button>
											)}
										</div>

										{/* Next/Close Button - Desktop only */}
										<button
											onClick={isPlaying || youtubeInitialized ? handleCloseVideo : handleNextVideo}
											className='absolute right-8 top-1/2 -translate-y-1/2 z-[60] text-white hover:text-brown-light transition-colors'
											aria-label={isPlaying || youtubeInitialized ? "Close video" : "Next video"}
										>
											{isPlaying || youtubeInitialized ? (
												// Close button (X) when playing
												<svg
													width='40'
													height='40'
													viewBox='0 0 29 29'
													fill='none'
													xmlns='http://www.w3.org/2000/svg'
													className='lg:w-[40px] lg:h-[40px] w-[20px] h-[20px]'
												>
													<path
														d='M2.5 2.5L26.5 26.5M26.5 2.5L2.5 26.5'
														stroke='currentColor'
														strokeWidth='3'
														strokeLinecap='round'
													/>
												</svg>
											) : (
												// Next button (arrow) when not playing
												<svg
													width='29'
													height='61'
													viewBox='0 0 29 61'
													fill='none'
													xmlns='http://www.w3.org/2000/svg'
													className='lg:w-[29px] lg:h-[61px] w-[17.5px] h-[36.5px]'
												>
													<path
														d='M3.73918 0.898035L28.3265 28.6713C28.7583 29.1591 29 29.8134 29 30.4947C29 31.1759 28.7583 31.8302 28.3265 32.318L3.73918 60.1017C3.53802 60.3292 3.29751 60.51 3.03184 60.6335C2.76618 60.757 2.48075 60.8206 2.19239 60.8206C1.90403 60.8206 1.6186 60.757 1.35293 60.6335C1.08727 60.51 0.846758 60.3292 0.645599 60.1017C0.231716 59.6352 1.90735e-06 59.0089 1.90735e-06 58.3568C1.90735e-06 57.7046 0.231716 57.0783 0.645599 56.6118L23.7668 30.4947L0.645599 4.38275C0.233135 3.91648 0.00231743 3.2913 0.00231743 2.64039C0.00231743 1.98949 0.233135 1.36431 0.645599 0.898035C0.846758 0.670543 1.08727 0.48973 1.35293 0.366269C1.6186 0.242807 1.90403 0.179199 2.19239 0.179199C2.48075 0.179199 2.76618 0.242807 3.03184 0.366269C3.29751 0.48973 3.53802 0.670543 3.73918 0.898035Z'
														fill='currentColor'
													/>
												</svg>
											)}
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Mobile text content - Hidden on desktop */}
					<div className='container lg:hidden'>
						<AnimatePresence mode='wait'>
							<motion.div
								key={`mobile-text-${activeVideo}`}
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								transition={{ duration: 0.5, ease: 'easeInOut' }}
								className='my-12 text-center'
							>
								<p className='text-2xl font-brandon mb-4'>
									{getTitle(activeVideo)}
								</p>
								<div
									className='text-lg text-gray-slate leading-relaxed prose'
									dangerouslySetInnerHTML={sanitizeHTML(getText(activeVideo))}
								/>
							</motion.div>
						</AnimatePresence>
						<div className='w-fit max-md:w-full'>
							<a
								href={data.button?.href || '#'}
								className='inline-block px-12 py-5 bg-brown rounded-lg text-xl text-white font-brandon hover:bg-brown/90 transition-colors w-full text-center'
							>
								{data.button?.label || ''}
							</a>
						</div>
					</div>
				</div>
			</div>
		</section>
	)
}

// Add TypeScript definition for YouTube iframe API
declare global {
  interface Window {
    YT: {
      Player: any;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
      }
    };
    onYouTubeIframeAPIReady: () => void;
  }
}